/**
 * Nexus — Centralised error extraction.
 *
 * FastAPI can return `detail` in three shapes:
 *   - string (our own HTTPException with a plain message)
 *   - object `{reason, message, ...}` (structured business error, e.g. anti-abuse)
 *   - array of `{loc, msg, type, ...}` (Pydantic validation error, HTTP 422)
 *
 * Feeding any of the last two into `toast.error(...)` (sonner) crashes React with
 * "Objects are not valid as a React child" (React error #31). Every place in
 * the app that surfaces an axios error must go through this helper.
 */
export function extractErrorMessage(err: any, fallback: string): string {
  if (!err) return fallback;
  // Axios error → err.response.data.detail
  const detail = err?.response?.data?.detail;
  if (detail) {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const first = detail[0];
      if (first && typeof first === 'object') {
        return String(first.msg || first.message || fallback);
      }
      return fallback;
    }
    if (typeof detail === 'object') {
      const msg = (detail as any).message || (detail as any).reason;
      if (typeof msg === 'string') return msg;
    }
  }
  // Plain Error
  if (typeof err?.message === 'string' && err.message) return err.message;
  return fallback;
}
